/**
 * useClassifyItem — shared hook for calling the ASIN/UPC classification API,
 * managing loading/error state, and navigating to the results page.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/store";
import { getItemClassification } from "src/services/itemClassificationService";
import { extractSiteCode } from "src/constants/constants";
import { SDS_TOTAL_DURATION_MS } from "./ClassificationProgressScreen";

export const useClassifyItem = () => {
  const navigate = useNavigate();
  const { siteLocation } = useAppSelector((state) => state.user);
  const siteCode = extractSiteCode(siteLocation);

  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productInfo, setProductInfo] = useState<{ imageUrl?: string; productName?: string; asin?: string } | null>(null);

  // Stores the analyzing-delay timer ID so we can cancel it on unmount,
  // preventing a stale navigate() call if the user leaves the page early.
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const classify = useCallback(
    async (asin: string) => {
      const value = asin.trim();
      if (!value) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getItemClassification(value, siteCode);

        // Capture product info for the analyzing screen
        setProductInfo({
          imageUrl: data.images?.[0]?.url,
          productName: data.name,
          asin: data.asin,
        });

        // Show the analyzing animation before navigating to results
        setIsLoading(false);
        setIsAnalyzing(true);

        await new Promise<void>((resolve) => {
          timeoutRef.current = setTimeout(resolve, SDS_TOTAL_DURATION_MS + 300);
        });

        navigate("/classification/results", { state: { data } });
      } catch (err: any) {
        setError(err?.message || "Failed to fetch classification data");
        setIsLoading(false);
        throw err; 
      }
    },
    [siteCode, navigate]
  );

  return { classify, isLoading, isAnalyzing, error, productInfo };
};
